import { useTranslation } from 'react-i18next';
import { DiagnosisField } from '@/components/patient/ReferralDiagnosisField';
import { fileLimitText } from '@/components/patient/HospitalReferralAttachments';
import { ZipContentSelect } from '@/components/patient/ZipContentSelect';
import { Button } from '@/components/ui/Button';
import { diagnoseTextForReferral } from '@/lib/referral/xref01Validation';
import {
  EPIKRISE_DIAGNOSE_CODE_MAX,
  EPIKRISE_DIAGNOSE_TEXT_MAX,
  EPIKRISE_DIAGNOSE_TYPE_CODES,
  EPIKRISE_OTHER_DESCRIPTION_CODES,
  EPIKRISE_OTHER_MAX,
  emptyOtherDiagnosis,
  type EpikriseForm,
  type EpikriseIssue,
  type EpikriseOtherDiagnosisInput,
} from '@/lib/referral/epikrise';
import { REFERRAL_FILE_MAX_COUNT, XML_ATTACHMENT_ACCEPT, needsZipObjectType } from '@/lib/referral/referralAttachments';
import type { EpikriseAttachment, EpikriseAttachmentIssue } from '@/lib/referral/epikriseAttachments';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { FIELD, HEADING, SUB } from '@/components/referrals/epikrisePanelShared';

/**
 * DIS05-S 4.17: yderligere undersøgelsesdiagnoser/behandlingskoder with date and time
 * (XDIS05 Diagnose/Other: DiagnoseDescriptionCode, DiagnoseCode, DiagnoseTypeCode,
 * DiagnoseText, DiagnoseDateTime).
 */
export function EpikriseOtherDiagnosesField({
  form,
  update,
  attempted,
  issues,
}: {
  form: EpikriseForm;
  update: (patch: Partial<EpikriseForm>) => void;
  attempted: boolean;
  issues: EpikriseIssue[];
}) {
  const { t } = useTranslation();
  const rows = form.otherDiagnoses;
  const set = (i: number, patch: Partial<EpikriseOtherDiagnosisInput>) =>
    update({ otherDiagnoses: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div className={SUB} data-testid="epikrise-other-diagnoses">
      <div className={HEADING}>
        {t('referrals.epikrise_other_title', 'Yderligere undersøgelsesdiagnoser og behandlingskoder ({{count}} af højst {{max}})', {
          count: rows.length,
          max: EPIKRISE_OTHER_MAX,
        })}
      </div>
      <p className={FORM_HINT_TYPE}>
        {t(
          'referrals.epikrise_other_hint',
          'Sendes som Diagnose/Other med dato og klokkeslæt (DiagnoseDateTime), fx dato og tid for den enkelte undersøgelse.',
        )}
      </p>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded border border-ds-line p-2 sm:grid-cols-6" data-testid="epikrise-other-row">
          <select
            aria-label={t('referrals.epikrise_other_kind', 'Type')}
            value={r.descriptionCode}
            onChange={(e) => set(i, { descriptionCode: e.target.value as EpikriseOtherDiagnosisInput['descriptionCode'] })}
            className={cn(TYPE.inputValue, FIELD, 'sm:col-span-2')}
          >
            {EPIKRISE_OTHER_DESCRIPTION_CODES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            aria-label={t('referrals.epikrise_other_code_type', 'Kodetype')}
            value={r.typeCode}
            onChange={(e) => set(i, { typeCode: e.target.value as EpikriseOtherDiagnosisInput['typeCode'] })}
            className={cn(TYPE.inputValue, FIELD)}
          >
            {EPIKRISE_DIAGNOSE_TYPE_CODES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            aria-label={t('referrals.epikrise_other_code', 'Kode')}
            placeholder={t('referrals.epikrise_other_code', 'Kode')}
            maxLength={EPIKRISE_DIAGNOSE_CODE_MAX}
            value={r.code}
            onChange={(e) => set(i, { code: e.target.value })}
            className={cn(TYPE.inputValue, FIELD, 'font-mono')}
            data-testid="epikrise-other-code"
          />
          <input
            aria-label={t('referrals.epikrise_other_text', 'Tekst')}
            placeholder={t('referrals.epikrise_other_text', 'Tekst')}
            maxLength={EPIKRISE_DIAGNOSE_TEXT_MAX}
            value={r.text}
            onChange={(e) => set(i, { text: e.target.value })}
            className={cn(TYPE.inputValue, FIELD, 'sm:col-span-2')}
            data-testid="epikrise-other-text"
          />
          <input
            type="date"
            aria-label={t('referrals.epikrise_other_date', 'Dato')}
            value={r.date}
            onChange={(e) => set(i, { date: e.target.value })}
            className={cn(TYPE.inputValue, FIELD, 'sm:col-span-2')}
            data-testid="epikrise-other-date"
          />
          <input
            type="time"
            aria-label={t('referrals.epikrise_other_time', 'Klokkeslæt')}
            value={r.time}
            onChange={(e) => set(i, { time: e.target.value })}
            className={cn(TYPE.inputValue, FIELD)}
            data-testid="epikrise-other-time"
          />
          <div className="flex items-end sm:col-span-3">
            <Button type="button" size="sm" variant="secondary" onClick={() => update({ otherDiagnoses: rows.filter((_, j) => j !== i) })}>
              {t('referrals.epikrise_remove', 'Fjern')}
            </Button>
          </div>
        </div>
      ))}
      {rows.length < EPIKRISE_OTHER_MAX && (
        <div className="space-y-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => update({ otherDiagnoses: [...rows, emptyOtherDiagnosis()] })}
            data-testid="epikrise-other-add"
          >
            {t('referrals.epikrise_other_add', 'Tilføj undersøgelsesdiagnose/behandlingskode')}
          </Button>
          <DiagnosisField
            key={`other-${rows.length}`}
            inputId="epikrise-other-sks"
            label={t('referrals.epikrise_other_add_sks', 'Eller tilføj fra SKS-diagnoser')}
            selected={null}
            onSelect={(d) =>
              update({ otherDiagnoses: [...rows, { ...emptyOtherDiagnosis(), code: d.code, text: diagnoseTextForReferral(d.textDa) }] })
            }
            onClear={() => undefined}
          />
        </div>
      )}
      {attempted && issues.includes('other_incomplete') && (
        <p role="alert" className={FORM_ERROR_TYPE}>
          {t('referrals.epikrise_other_incomplete', 'Angiv både kode og tekst for hver yderligere diagnose, eller fjern rækken.')}
        </p>
      )}
      {attempted && issues.includes('other_too_long') && (
        <p role="alert" className={FORM_ERROR_TYPE}>
          {t('referrals.epikrise_other_too_long', 'Koden må højst være {{code}} tegn og teksten højst {{text}} tegn.', {
            code: EPIKRISE_DIAGNOSE_CODE_MAX,
            text: EPIKRISE_DIAGNOSE_TEXT_MAX,
          })}
        </p>
      )}
      {attempted && issues.includes('other_time_without_date') && (
        <p role="alert" className={FORM_ERROR_TYPE}>
          {t('referrals.epikrise_other_time_without_date', 'Et klokkeslæt kræver en dato.')}
        </p>
      )}
    </div>
  );
}

function epikriseAttachmentIssueText(
  t: (key: string, fallback: string) => string,
  issue: EpikriseAttachmentIssue,
): string {
  switch (issue) {
    case 'zip_type_missing':
      return t('referrals.epikrise_attachment_zip_type', 'Vælg hvad zip-filen indeholder.');
    case 'name_not_latin1':
      return t('referrals.epikrise_attachment_name_latin1', 'Filnavnet indeholder tegn, der ikke kan sendes (ISO-8859-1). Omdøb filen.');
    case 'empty':
      return t('referrals.epikrise_attachment_empty', 'Filen er tom.');
    default:
      return fileLimitText(t, issue);
  }
}

/** DIS05-S 2.5/4.26/4.30/4.31: bilag as XBIN01, cited in the epikrise's Reference/BIN. */
export function EpikriseAttachmentsField({
  attachments,
  setAttachments,
  issues,
  attempted,
}: {
  attachments: EpikriseAttachment[];
  setAttachments: (next: EpikriseAttachment[]) => void;
  issues: Record<string, EpikriseAttachmentIssue>;
  attempted: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={SUB} data-testid="epikrise-attachments">
      <div className={HEADING}>
        {t('referrals.epikrise_attachments', 'Bilag (XBIN01) – {{count}} af højst {{max}}', {
          count: attachments.length,
          max: REFERRAL_FILE_MAX_COUNT,
        })}
      </div>
      <p className={FORM_HINT_TYPE}>
        {t(
          'referrals.epikrise_attachments_hint',
          'Hvert bilag dannes som XBIN01 og linkes fra epikrisen (Reference/BIN med samme id). Højst 10 filer og 100 MB tilsammen. Bilag gemmes ikke i kladden; vælg dem, før epikrisen dannes.',
        )}
      </p>
      {attachments.length > 0 && (
        <ul className="space-y-2">
          {attachments.map((a) => (
            <li key={a.key} className="text-sm text-ds-text" data-testid="epikrise-attachment-row">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="break-all">
                  {a.fileName} · {Math.max(1, Math.round(a.byteLength / 1024))} KB
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setAttachments(attachments.filter((x) => x.key !== a.key))}
                >
                  {t('referrals.epikrise_remove', 'Fjern')}
                </Button>
              </div>
              {needsZipObjectType(a.fileName) && (
                <ZipContentSelect
                  id={`epikrise-zip-${a.key}`}
                  value={a.zipObjectType}
                  onChange={(zipObjectType) =>
                    setAttachments(attachments.map((x) => (x.key === a.key ? { ...x, zipObjectType } : x)))
                  }
                />
              )}
              {issues[a.key] && (attempted || issues[a.key] !== 'zip_type_missing') && (
                <p role="alert" className={FORM_ERROR_TYPE}>
                  {epikriseAttachmentIssueText(t, issues[a.key]!)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {attachments.length < REFERRAL_FILE_MAX_COUNT && (
        <label className="block">
          <span className={cn('block', FORM_LABEL_TYPE)}>{t('referrals.epikrise_attachment_add', 'Vedhæft fil')}</span>
          <input
            type="file"
            multiple
            accept={XML_ATTACHMENT_ACCEPT}
            className="mt-1 block text-sm"
            data-testid="epikrise-attachment-input"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length === 0) return;
              setAttachments([
                ...attachments,
                ...files.map((file) => ({
                  key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  fileName: file.name,
                  byteLength: file.size,
                  file,
                })),
              ]);
            }}
          />
        </label>
      )}
    </div>
  );
}
